"use client"

import * as React from "react"
import { CalendarIcon, ChevronDownIcon, XIcon } from "lucide-react"
import { format, isValid, parse } from "date-fns"
import { cn } from "@workspace/ui/lib/utils"
import { Calendar } from "@workspace/ui/components/calendar"
import { UIPopover, UIPopoverContent, UIPopoverTrigger } from "../layout/ui-popover"
import { UIButton } from "../buttons/ui-button"
import { UIInput } from "./ui-input"
import { UIInputGroup, UIInputGroupInput, UIInputGroupAddon } from "./ui-input-group"
import { UILabel } from "./ui-label"
import { UITypography } from "../display/ui-typography"
import { UIBox } from "../layout/ui-box"

// ============================================================================
// Base Calendar Component (re-export with UI* naming)
// ============================================================================

export const UICalendar = Calendar

// ============================================================================
// Date Picker Components
// ============================================================================

// Single Date Picker
export interface UIDatePickerProps {
  selected?: Date
  onSelect?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  buttonVariant?: "default" | "outline" | "ghost"
  dateFormat?: string
  minDate?: Date
  maxDate?: Date
  disabledDates?: Date[]
  allowClear?: boolean
  id?: string
}

export const UIDatePicker = React.forwardRef<
  HTMLButtonElement,
  UIDatePickerProps
>(({
  selected,
  onSelect,
  placeholder = "Pick a date",
  disabled = false,
  className,
  buttonVariant = "outline",
  dateFormat = "PPP", // "January 1, 2024"
  minDate,
  maxDate,
  disabledDates,
  allowClear = true,
  id,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false)

  const handleSelect = (date: Date | undefined) => {
    onSelect?.(date)
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect?.(undefined)
  }

  const formatDate = (date: Date | undefined) => {
    if (!date || !isValid(date)) return null
    return format(date, dateFormat)
  }

  const disabledDateMatcher = React.useMemo(() => {
    const matchers = []

    if (minDate) {
      matchers.push({ before: minDate })
    }

    if (maxDate) {
      matchers.push({ after: maxDate })
    }

    if (disabledDates?.length) {
      matchers.push(...disabledDates)
    }

    return matchers.length > 0 ? matchers : undefined
  }, [minDate, maxDate, disabledDates])

  return (
    <UIPopover open={open} onOpenChange={setOpen}>
      <UIPopoverTrigger asChild>
        <UIButton
          ref={ref}
          id={id}
          variant={buttonVariant}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selected && "text-muted-foreground",
            // Width stability pattern
            "w-full max-w-full min-w-0 flex-shrink-0",
            className
          )}
          disabled={disabled}
          {...props}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selected ? formatDate(selected) : placeholder}
          {allowClear && selected && (
            <XIcon
              className="ml-auto h-4 w-4 hover:text-destructive"
              onClick={handleClear}
            />
          )}
        </UIButton>
      </UIPopoverTrigger>
      <UIPopoverContent className="w-auto p-0 max-w-none" align="start">
        <UICalendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          disabled={disabledDateMatcher}
          initialFocus
        />
      </UIPopoverContent>
    </UIPopover>
  )
})
UIDatePicker.displayName = "UIDatePicker"

// Date Picker with Input
export interface UIDatePickerInputProps {
  selected?: Date
  onSelect?: (date: Date | undefined) => void
  placeholder?: string
  label?: string
  disabled?: boolean
  className?: string
  dateFormat?: string
  inputFormat?: string // Format for input parsing (e.g., "dd/MM/yyyy")
  minDate?: Date
  maxDate?: Date
  disabledDates?: Date[]
  error?: string
  required?: boolean
  id?: string
}

export const UIDatePickerInput = React.forwardRef<
  HTMLInputElement,
  UIDatePickerInputProps
>(({
  selected,
  onSelect,
  placeholder = "dd/mm/yyyy",
  label,
  disabled = false,
  className,
  dateFormat = "PPP",
  inputFormat = "dd/MM/yyyy",
  minDate,
  maxDate,
  disabledDates,
  error,
  required = false,
  id,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")

  // Update input value when selected date changes
  React.useEffect(() => {
    if (selected && isValid(selected)) {
      setInputValue(format(selected, inputFormat))
    } else {
      setInputValue("")
    }
  }, [selected, inputFormat])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)

    // Try to parse the input value
    if (value) {
      const parsedDate = parse(value, inputFormat, new Date())
      if (isValid(parsedDate)) {
        onSelect?.(parsedDate)
      }
    } else {
      onSelect?.(undefined)
    }
  }

  const handleCalendarSelect = (date: Date | undefined) => {
    onSelect?.(date)
    setOpen(false)
  }

  const disabledDateMatcher = React.useMemo(() => {
    const matchers = []

    if (minDate) {
      matchers.push({ before: minDate })
    }

    if (maxDate) {
      matchers.push({ after: maxDate })
    }

    if (disabledDates?.length) {
      matchers.push(...disabledDates)
    }

    return matchers.length > 0 ? matchers : undefined
  }, [minDate, maxDate, disabledDates])

  return (
    <UIBox className={cn("space-y-2", className)}>
      {label && (
        <UILabel htmlFor={id} className={required ? "after:content-['*'] after:text-destructive after:ml-1" : undefined}>
          {label}
        </UILabel>
      )}

      <UIInputGroup>
        <UIInputGroupInput
          ref={ref}
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          hasRightAddon
          className={cn(
            error && "border-destructive focus-visible:ring-destructive"
          )}
          {...props}
        />

        <UIInputGroupAddon position="right">
          <UIPopover open={open} onOpenChange={setOpen}>
            <UIPopoverTrigger asChild>
              <UIButton
                variant="ghost"
                size="icon"
                className="h-7 w-7 hover:translate-y-0 hover:scale-100"
                disabled={disabled}
                type="button"
              >
                <CalendarIcon className="h-4 w-4" />
                <UITypography as="span" variant="small" className="sr-only">Open calendar</UITypography>
              </UIButton>
            </UIPopoverTrigger>
            <UIPopoverContent className="w-auto p-0 max-w-none" align="start">
              <UICalendar
                mode="single"
                selected={selected}
                onSelect={handleCalendarSelect}
                disabled={disabledDateMatcher}
                initialFocus
              />
            </UIPopoverContent>
          </UIPopover>
        </UIInputGroupAddon>
      </UIInputGroup>

      {error && (
        <UITypography variant="p" size="sm" className="text-destructive">{error}</UITypography>
      )}
    </UIBox>
  )
})
UIDatePickerInput.displayName = "UIDatePickerInput"

// Date Range Picker
export interface UIDateRangePickerProps {
  selected?: { from?: Date; to?: Date }
  onSelect?: (range: { from?: Date; to?: Date } | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  buttonVariant?: "default" | "outline" | "ghost"
  dateFormat?: string
  minDate?: Date
  maxDate?: Date
  disabledDates?: Date[]
  numberOfMonths?: number
  id?: string
}

export const UIDateRangePicker = React.forwardRef<
  HTMLButtonElement,
  UIDateRangePickerProps
>(({
  selected,
  onSelect,
  placeholder = "Pick a date range",
  disabled = false,
  className,
  buttonVariant = "outline",
  dateFormat = "LLL dd, y",
  minDate,
  maxDate,
  disabledDates,
  numberOfMonths = 2,
  id,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false)

  const handleRangeSelect = (range: { from?: Date; to?: Date } | undefined) => {
    onSelect?.(range)
    // Close popover only when both dates are selected
    if (range?.from && range?.to) {
      setOpen(false)
    }
  }

  const formatRange = (range: { from?: Date; to?: Date } | undefined) => {
    if (!range?.from) return null

    const fromFormatted = format(range.from, dateFormat)

    if (!range.to) {
      return fromFormatted
    }

    const toFormatted = format(range.to, dateFormat)
    return `${fromFormatted} - ${toFormatted}`
  }

  const disabledDateMatcher = React.useMemo(() => {
    const matchers = []

    if (minDate) {
      matchers.push({ before: minDate })
    }

    if (maxDate) {
      matchers.push({ after: maxDate })
    }

    if (disabledDates?.length) {
      matchers.push(...disabledDates)
    }

    return matchers.length > 0 ? matchers : undefined
  }, [minDate, maxDate, disabledDates])

  return (
    <UIPopover open={open} onOpenChange={setOpen}>
      <UIPopoverTrigger asChild>
        <UIButton
          ref={ref}
          id={id}
          variant={buttonVariant}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selected?.from && "text-muted-foreground",
            // Width stability pattern
            "w-full max-w-full min-w-0 flex-shrink-0",
            className
          )}
          disabled={disabled}
          {...props}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selected?.from ? formatRange(selected) : placeholder}
          <ChevronDownIcon className="ml-auto h-4 w-4 opacity-50" />
        </UIButton>
      </UIPopoverTrigger>
      <UIPopoverContent className="w-auto p-0 max-w-none" align="start">
        <UICalendar
          mode="range"
          defaultMonth={selected?.from}
          selected={selected as any}
          onSelect={handleRangeSelect}
          numberOfMonths={numberOfMonths}
          disabled={disabledDateMatcher}
          initialFocus
        />
      </UIPopoverContent>
    </UIPopover>
  )
})
UIDateRangePicker.displayName = "UIDateRangePicker"

// Inline Calendar
export interface UICalendarInlineProps {
  selected?: Date
  onSelect?: (date: Date | undefined) => void
  className?: string
  minDate?: Date
  maxDate?: Date
  disabledDates?: Date[]
  showOutsideDays?: boolean
  fixedWeeks?: boolean
}

export const UICalendarInline = React.forwardRef<
  HTMLDivElement,
  UICalendarInlineProps
>(({
  selected,
  onSelect,
  className,
  minDate,
  maxDate,
  disabledDates,
  showOutsideDays = true,
  fixedWeeks = false,
  ...props
}, ref) => {
  const disabledDateMatcher = React.useMemo(() => {
    const matchers = []

    if (minDate) {
      matchers.push({ before: minDate })
    }

    if (maxDate) {
      matchers.push({ after: maxDate })
    }

    if (disabledDates?.length) {
      matchers.push(...disabledDates)
    }

    return matchers.length > 0 ? matchers : undefined
  }, [minDate, maxDate, disabledDates])

  return (
    <UIBox
      ref={ref}
      className={cn(
        "rounded-md border",
        // Width stability pattern
        "w-full max-w-full min-w-0 flex-shrink-0",
        className
      )}
      {...props}
    >
      <UICalendar
        mode="single"
        selected={selected}
        onSelect={onSelect}
        disabled={disabledDateMatcher}
        showOutsideDays={showOutsideDays}
        fixedWeeks={fixedWeeks}
        className="w-full"
      />
    </UIBox>
  )
})
UICalendarInline.displayName = "UICalendarInline"

// Preset Date Picker (for common date selections)
export interface UIDatePickerPresetProps {
  selected?: Date
  onSelect?: (date: Date | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  presets?: Array<{
    label: string
    value: Date | (() => Date)
  }>
  id?: string
}

export const UIDatePickerPreset = React.forwardRef<
  HTMLButtonElement,
  UIDatePickerPresetProps
>(({
  selected,
  onSelect,
  placeholder = "Pick a date",
  disabled = false,
  className,
  presets = [
    { label: "Today", value: () => new Date() },
    { label: "Tomorrow", value: () => new Date(Date.now() + 24 * 60 * 60 * 1000) },
    { label: "In a week", value: () => new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) },
    { label: "In a month", value: () => new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) },
  ],
  id,
  ...props
}, ref) => {
  const [open, setOpen] = React.useState(false)

  const handlePresetSelect = (preset: { label: string; value: Date | (() => Date) }) => {
    const date = typeof preset.value === 'function' ? preset.value() : preset.value
    onSelect?.(date)
    setOpen(false)
  }

  const handleCalendarSelect = (date: Date | undefined) => {
    onSelect?.(date)
    setOpen(false)
  }

  return (
    <UIPopover open={open} onOpenChange={setOpen}>
      <UIPopoverTrigger asChild>
        <UIButton
          ref={ref}
          id={id}
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !selected && "text-muted-foreground",
            // Width stability pattern
            "w-full max-w-full min-w-0 flex-shrink-0",
            className
          )}
          disabled={disabled}
          {...props}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selected ? format(selected, "PPP") : placeholder}
        </UIButton>
      </UIPopoverTrigger>
      <UIPopoverContent className="w-auto p-0 max-w-none" align="start">
        <UIBox className="flex">
          {/* Presets */}
          <UIBox className="border-r p-3 space-y-1 min-w-[120px]">
            <UITypography variant="h4" size="sm" weight="medium" className="mb-2">Quick Select</UITypography>
            {presets.map((preset) => (
              <UIButton
                key={preset.label}
                variant="ghost"
                size="sm"
                className="w-full justify-start h-auto py-1 px-2 text-sm"
                onClick={() => handlePresetSelect(preset)}
              >
                {preset.label}
              </UIButton>
            ))}
          </UIBox>

          {/* Calendar */}
          <UIBox>
            <UICalendar
              mode="single"
              selected={selected}
              onSelect={handleCalendarSelect}
              initialFocus
            />
          </UIBox>
        </UIBox>
      </UIPopoverContent>
    </UIPopover>
  )
})
UIDatePickerPreset.displayName = "UIDatePickerPreset"

// ============================================================================
// Type Exports
// ============================================================================

export type UICalendarProps = React.ComponentProps<typeof Calendar>